import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist/build/pdf.mjs";
import { ZoomIn, ZoomOut, Maximize } from "lucide-react";
import { Button } from "../ui/button";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const ZOOMS = [0.6, 0.8, 1, 1.25, 1.5, 2, 2.5, 3];

// Renderiza um PDF (Blob) em canvas — funciona em desktop e telemóvel sem plugin do browser
export const PdfCanvasViewer = ({ blob }) => {
  const host = useRef(null);
  const scroller = useRef(null);
  const pdfRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [state, setState] = useState({ pages: 0, error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = new Uint8Array(await blob.arrayBuffer());
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setZoom(1);
        setState({ pages: pdf.numPages, error: "" });
      } catch (e) {
        if (!cancelled) setState({ pages: 0, error: e?.message || "Não foi possível mostrar o PDF" });
      }
    })();
    return () => { cancelled = true; pdfRef.current = null; };
  }, [blob]);

  useEffect(() => {
    const pdf = pdfRef.current;
    const el = host.current;
    if (!pdf || !el || !state.pages) return undefined;
    let cancelled = false;
    el.innerHTML = "";
    (async () => {
      const fitWidth = Math.min((scroller.current?.clientWidth || 900) - 24, 900);
      const dpr = window.devicePixelRatio || 1;
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        const scale = (fitWidth / base.width) * zoom;
        const vp = page.getViewport({ scale: scale * dpr });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width; canvas.height = vp.height;
        canvas.style.width = `${vp.width / dpr}px`; canvas.style.height = `${vp.height / dpr}px`;
        canvas.className = "bg-white shadow-lg block mx-auto";
        canvas.dataset.testid = `pdf-page-${i}`;
        el.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
      }
    })();
    return () => { cancelled = true; };
  }, [state.pages, zoom]);

  const step = (dir) => setZoom((z) => ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, ZOOMS.indexOf(z) + dir))]);

  return (
    <div className="h-full flex flex-col" data-testid="pdf-canvas-viewer" data-pages={state.pages} data-zoom={zoom}>
      <div ref={scroller} className="flex-1 min-h-0 overflow-auto p-3">
        {state.error && <p className="text-center text-sm text-red-300" data-testid="pdf-viewer-error">{state.error}</p>}
        <div ref={host} className="space-y-3 w-max min-w-full" />
      </div>
      {state.pages > 0 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-zinc-950/90 border border-white/10 px-2 py-1 text-white shadow-xl" data-testid="pdf-zoom-controls">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" onClick={() => step(-1)} disabled={zoom === ZOOMS[0]} aria-label="Reduzir" data-testid="pdf-zoom-out-btn"><ZoomOut className="w-4 h-4" /></Button>
          <span className="text-xs font-mono w-12 text-center" data-testid="pdf-zoom-level">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" onClick={() => step(1)} disabled={zoom === ZOOMS[ZOOMS.length - 1]} aria-label="Ampliar" data-testid="pdf-zoom-in-btn"><ZoomIn className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" onClick={() => setZoom(1)} aria-label="Ajustar à largura" data-testid="pdf-zoom-fit-btn"><Maximize className="w-4 h-4" /></Button>
          <span className="text-[10px] text-white/50 pl-1 pr-1 hidden sm:inline">{state.pages} pág.</span>
        </div>
      )}
    </div>
  );
};
